import React, { useEffect, useState } from "react";
import {
  Table,
  Space,
  Button,
  Tag,
  Avatar,
  Typography,
  Card,
  Row,
  Col,
  Select,
  Input,
  Dropdown,
  MenuProps,
  Tooltip,
  Statistic,
  message,
} from "antd";
import { useList, useDelete, useNavigation } from "@refinedev/core";
import {
  PlusOutlined,
  SearchOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  UserOutlined,
  CheckCircleOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { UserManagement, UserFilters } from "@/types";
import { apiService } from "@/services/api";

const { Title, Text } = Typography;
const { Option } = Select;

export const UserList: React.FC = () => {
  const [filters, setFilters] = useState<UserFilters>({});
  const [searchValue, setSearchValue] = useState("");

  const { mutate: deleteUser } = useDelete();
  const { push } = useNavigation();

  // Les abonnes de l'application et l'equipe du backoffice n'ont rien a voir :
  // les melanger dans une seule liste rendait la gestion des comptes illisible.
  // La distinction se fait sur les roles, cote serveur.
  const [population, setPopulation] = useState<string>("APPLICATION");

  // Les options du filtre par role viennent du realm : elles etaient devinees,
  // et deux des trois proposees n'existaient pas.
  const [rolesDisponibles, setRolesDisponibles] = useState<
    Array<{ name: string; description?: string }>
  >([]);

  useEffect(() => {
    (async () => {
      try {
        setRolesDisponibles(await apiService.getRealmRoles());
      } catch (e) {
        console.error("Chargement des rôles impossible", e);
      }
    })();
  }, []);

  const {
    data: usersData,
    isLoading,
    refetch,
  } = useList<UserManagement>({
    resource: "users",
    pagination: { current: 1, pageSize: 20 },
    filters: [
      { field: "search", operator: "contains", value: searchValue },
      { field: "population", operator: "eq", value: population },
      { field: "status", operator: "eq", value: filters.status },
      { field: "role", operator: "eq", value: filters.role },
    ],
  });

  const handleDelete = (id: string) => {
    deleteUser({
      resource: "users",
      id,
      successNotification: () => ({
        message: "Utilisateur supprimé",
        description: "L'utilisateur a été supprimé avec succès",
        type: "success",
      }),
    });
  };

  const handleToggleEnabled = async (id: string, currentEnabled: boolean) => {
    try {
      await apiService.setUserEnabled(id, !currentEnabled);
      message.success(
        currentEnabled
          ? "Utilisateur désactivé avec succès"
          : "Utilisateur activé avec succès"
      );
      refetch();
    } catch (error: any) {
      message.error("Erreur lors du changement de statut");
    }
  };

  const getStatusColor = (enabled: boolean) => {
    return enabled ? "green" : "red";
  };

  const getStatusIcon = (enabled: boolean) => {
    return enabled ? <CheckCircleOutlined /> : <StopOutlined />;
  };

  const getStatusText = (enabled: boolean) => {
    return enabled ? "ACTIF" : "DÉSACTIVÉ";
  };

  const getActionMenu = (record: UserManagement): MenuProps["items"] => [
    {
      key: "view",
      icon: <EyeOutlined />,
      label: "Voir",
      onClick: () => push(`/users/show/${record.id}`),
    },
    {
      key: "edit",
      icon: <EditOutlined />,
      label: "Modifier",
      onClick: () => push(`/users/edit/${record.id}`),
    },
    {
      key: "toggle",
      icon: record.enabled ? <StopOutlined /> : <CheckCircleOutlined />,
      label: record.enabled ? "Désactiver" : "Activer",
      onClick: () => handleToggleEnabled(record.id, record.enabled),
    },
    {
      type: "divider",
    },
    {
      key: "delete",
      icon: <DeleteOutlined />,
      label: "Supprimer",
      danger: true,
      onClick: () => handleDelete(record.id),
    },
  ];

  const columns = [
    {
      title: "",
      dataIndex: "checkbox",
      key: "checkbox",
      width: 50,
      render: () => <input type="checkbox" />,
    },
    {
      title: "UTILISATEUR",
      dataIndex: "user",
      key: "user",
      render: (_: any, record: UserManagement) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar src={record.profileImageUrl}>
            {record.username?.charAt(0).toUpperCase()}
          </Avatar>
          <div>
            <div style={{ fontWeight: 500 }}>{record.username}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.email}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: "RÔLE",
      dataIndex: "roles",
      key: "roles",
      render: (roles: string[]) => (
        <>
          {roles && roles.length > 0 ? (
            roles.map((role) => (
              <Tag key={role} color="blue" style={{ marginBottom: 4 }}>
                {role.replace("ROLE_", "")}
              </Tag>
            ))
          ) : (
            <Tag>Aucun rôle</Tag>
          )}
        </>
      ),
    },
    {
      title: "STATUT",
      dataIndex: "enabled",
      key: "enabled",
      render: (enabled: boolean) => (
        <Tag
          color={getStatusColor(enabled)}
          icon={getStatusIcon(enabled)}
        >
          {getStatusText(enabled)}
        </Tag>
      ),
    },
    {
      title: "DATE DE CRÉATION",
      dataIndex: "createdTimestamp",
      key: "createdTimestamp",
      render: (date: string) => (
        <Text type="secondary">
          {date ? new Date(date).toLocaleDateString("fr-FR") : "N/A"}
        </Text>
      ),
    },
    {
      title: "DERNIÈRE CONNEXION",
      dataIndex: "lastLoginAt",
      key: "lastLoginAt",
      render: (date: string) => (
        <Text type="secondary">
          {date ? new Date(date).toLocaleDateString("fr-FR") : "Jamais"}
        </Text>
      ),
    },
    {
      title: "ACTIONS",
      key: "actions",
      render: (_: any, record: UserManagement) => (
        <Space>
          <Tooltip title="Voir les détails">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => push(`/users/show/${record.id}`)}
            />
          </Tooltip>
          <Tooltip title="Modifier">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => push(`/users/edit/${record.id}`)}
            />
          </Tooltip>
          <Dropdown
            menu={{ items: getActionMenu(record) }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        </Space>
      ),
    },
  ];

  // Ces quatre chiffres etaient ecrits en dur (24 521 comptes, 19 842 actifs...)
  // et ne correspondaient a rien. Ils viennent maintenant de Keycloak pour les
  // comptes et du service de paiement pour les abonnements.
  const [stats, setStats] = useState<{
    total: number;
    enabled: number;
    newLast30Days: number;
    growthPercent: number | null;
    abonnes: number;
  } | null>(null);
  const [statsEnErreur, setStatsEnErreur] = useState(false);

  useEffect(() => {
    let annule = false;
    const charger = async () => {
      try {
        // Les abonnements viennent d'un autre service : son indisponibilite ne
        // doit pas priver la page des chiffres Keycloak.
        const [comptes, abonnements] = await Promise.all([
          apiService.getUserStats(),
          apiService
            .getAdminSubscriptions(1, 1, "ACTIVE")
            .catch(() => ({ total: 0 })),
        ]);
        if (!annule) {
          setStats({
            total: comptes.total ?? 0,
            enabled: comptes.enabled ?? 0,
            newLast30Days: comptes.newLast30Days ?? 0,
            growthPercent: comptes.growthPercent ?? null,
            abonnes: Number(abonnements?.total ?? 0),
          });
        }
      } catch (error) {
        console.error("Statistiques utilisateurs indisponibles:", error);
        if (!annule) setStatsEnErreur(true);
      }
    };
    charger();
    return () => {
      annule = true;
    };
  }, []);

  // Tant que le chiffre n'est pas connu, on n'affiche pas un zero trompeur.
  const valeur = (v?: number) => (stats ? v ?? 0 : statsEnErreur ? "—" : "…");

  // Evolution reelle des inscriptions, uniquement s'il y a de quoi comparer.
  const evolutionInscriptions =
    stats?.growthPercent == null ? null : (
      <span
        style={{
          color: stats.growthPercent >= 0 ? "#52c41a" : "#ff4d4f",
          fontSize: 12,
        }}
      >
        {stats.growthPercent >= 0 ? "↑" : "↓"}
        {Math.abs(stats.growthPercent)}% vs 30 jours precedents
      </span>
    );

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0, marginBottom: 8 }}>
          Gestion des Utilisateurs
        </Title>
        <Text type="secondary">
          Gérez les profils, préférences et permissions des utilisateurs
        </Text>
      </div>

      <Card style={{ marginBottom: 24 }}>
        <Space wrap>
          <Button
            type={population === "APPLICATION" ? "primary" : "default"}
            onClick={() => setPopulation("APPLICATION")}
          >
            Abonnés de l'application
          </Button>
          <Button
            type={population === "BACKOFFICE" ? "primary" : "default"}
            onClick={() => setPopulation("BACKOFFICE")}
          >
            Équipe du backoffice
          </Button>
          <Button
            type={population === "TOUS" ? "primary" : "default"}
            onClick={() => setPopulation("TOUS")}
          >
            Tous les comptes
          </Button>
        </Space>
      </Card>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Utilisateurs"
              value={valeur(stats?.total)}
              prefix={<UserOutlined style={{ color: "#1890ff" }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Comptes activés"
              value={valeur(stats?.enabled)}
              prefix={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Inscriptions (30 jours)"
              value={valeur(stats?.newLast30Days)}
              prefix={<UserOutlined style={{ color: "#722ed1" }} />}
              suffix={evolutionInscriptions}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Abonnements actifs"
              value={valeur(stats?.abonnes)}
              prefix={<CheckCircleOutlined style={{ color: "#faad14" }} />}
            />
          </Card>
        </Col>
      </Row>

      {/* Action Buttons */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col flex="auto">
          <Space>
            <Button
              icon={<PlusOutlined />}
              type="primary"
              onClick={() => push(`/users/create`)}
            >
              Ajouter un utilisateur
            </Button>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button icon={<SearchOutlined />}>Rechercher</Button>
          </Space>
        </Col>
      </Row>

      {/* Search and Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Input
              placeholder="Rechercher un utilisateur..."
              prefix={<SearchOutlined />}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              style={{ maxWidth: 400 }}
            />
          </Col>
          <Col>
            <Space>
              {/* Keycloak ne connait qu'un compte actif ou inactif : les
                  options « Suspendu » et « En attente » n'existaient nulle
                  part. */}
              <Select
                placeholder="Statut"
                style={{ width: 150 }}
                allowClear
                value={filters.status}
                onChange={(value) => setFilters({ ...filters, status: value })}
              >
                <Option value="ACTIVE">Actif</Option>
                <Option value="INACTIVE">Inactif</Option>
              </Select>
              <Select
                placeholder="Rôle"
                style={{ width: 190 }}
                allowClear
                value={filters.role}
                onChange={(value) => setFilters({ ...filters, role: value })}
              >
                {rolesDisponibles.map((r) => (
                  <Option key={r.name} value={r.name}>
                    {r.description || r.name}
                  </Option>
                ))}
              </Select>
              {/* Le filtre « Abonnement » a ete retire : aucune colonne ne
                  montre l'abonnement et l'API ne sait pas filtrer dessus. Voir
                  BO-17 au backlog. */}
              <Button
                type="link"
                onClick={() => {
                  setFilters({});
                  setSearchValue("");
                  setPopulation("APPLICATION");
                }}
              >
                Réinitialiser
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Users Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={usersData?.data}
          loading={isLoading}
          rowKey="id"
          pagination={{
            total: usersData?.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} sur ${total} utilisateurs`,
          }}
        />
      </Card>
    </div>
  );
};
